import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { RotationRow, VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

import { resolveTripSpans } from './resolveTripSpan';
import { Lane, LongHaulTrip, WorkTarget } from './types';

type Input = {
  groups: VehicleRowGroup[];
  workTargets: WorkTarget[];
  legRunById: ReadonlyMap<number, ShipmentOrderLegRunForPlanEntity>;
};

const laneKeyOf = (truckId: number, driverId: number | null) => `${truckId}:${driverId ?? 'none'}`;

/** shiftTruckId → レーンのキー。運行を持たない勤務の置き場所を決めるのに使う */
const buildLaneKeyByShiftTruckId = (groups: VehicleRowGroup[]) => {
  const index = new Map<number, string>();
  for (const group of groups) {
    if (group.shiftTruckId == null) continue;
    const row = group.rows.find((r) => !r.source?.isPlaceholderRow);
    if (!row) continue;
    index.set(group.shiftTruckId, laneKeyOf(row.truckId, row.driverId ?? null));
  }
  return index;
};

/**
 * レーン分割の単位。**バーではなく回転**で見る。
 *
 * 1 回転は積地ごとに複数のバーへ分かれる（`resolveTripSpans`）。バーどうしで重なりを
 * 見ると、同じ勤務の中で積地が 2 つあるだけでレーンが 2 行に割れてしまう（同じトラックの
 * 同じ勤務なのに車両行が 2 つ出る）。同じ日を取り合うぶんは箱割り
 * （`buildLaneBoxLayout`）が 1 レーンの中で縦に分ける。
 */
type Rotation = { trips: LongHaulTrip[]; occupyStartAt: string; occupyEndAt: string };

/**
 * 端点の一致は重複としない（前の運行の終了時刻 = 次の運行の開始時刻）。
 * 比較は epoch 値で行う（ISO のオフセット混在で辞書順が崩れるため）。
 */
const overlaps = (a: Rotation, b: Rotation) =>
  Date.parse(a.occupyStartAt) < Date.parse(b.occupyEndAt)
  && Date.parse(b.occupyStartAt) < Date.parse(a.occupyEndAt);

export function buildLongHaulLanes({ groups, workTargets, legRunById }: Input): Lane[] {
  // 1) (truckId, driverId) で束ねる
  const buckets = new Map<string, {
    truckId: number; driverId: number | null; vehicle: Lane['vehicle'];
    rotations: Rotation[]; unplannedRows: RotationRow[];
  }>();

  for (const group of groups) {
    for (const row of group.rows) {
      if (row.source?.isPlaceholderRow) continue;
      const key = laneKeyOf(row.truckId, row.driverId ?? null);
      const bucket = buckets.get(key) ?? {
        truckId: row.truckId, driverId: row.driverId ?? null, vehicle: group.vehicle,
        rotations: [], unplannedRows: [],
      };
      // 1 回転は積地ごとに複数のバーへ分かれる
      const segments = resolveTripSpans(row, legRunById);
      if (segments.length === 0) {
        // operation が空・積だけ・バー移動後に ID が 0 件になった回転。
        // 捨てると「未計画」を描けなくなるので残す
        bucket.unplannedRows.push(row);
      }
      const tripsOfRow: LongHaulTrip[] = [];
      for (const resolved of segments) {
        const legRunsOfTrip = resolved.legRunIds
          .map((id) => legRunById.get(id))
          .filter((legRun): legRun is ShipmentOrderLegRunForPlanEntity => !!legRun);
        const first = legRunsOfTrip[0];
        tripsOfRow.push({
          // 回転 + 積地でバーを指す。積地キーは resolveTripSpans が付ける
          barId: `${row.rowId}::${resolved.loadingKey}`,
          rowId: row.rowId,
          shiftTruckId: row.shiftTruckId ?? null,
          legRunIds: resolved.legRunIds,
          span: resolved.span,
          shipperName: first?.shipper_name ?? '',
          loadingName: first?.loading_name ?? '',
          unloadingNames: Array.from(new Set(
            legRunsOfTrip.map((legRun) => legRun.unloading_name).filter((n): n is string => !!n),
          )),
          loadingPrefecture: first?.loading_administrative_area_level1 ?? null,
          unloadingPrefectures: Array.from(new Set(
            legRunsOfTrip
              .map((legRun) => legRun.unloading_administrative_area_level1)
              .filter((n): n is string => !!n),
          )),
          loadingArea: first?.loading_area ?? null,
          unloadingAreas: Array.from(new Set(
            legRunsOfTrip
              .map((legRun) => legRun.unloading_area)
              .filter((n): n is string => !!n),
          )),
          // legRunId は resolveTripSpan で重複を除いてあるので、そのまま足せる
          totalItemCount: legRunsOfTrip.reduce((sum, legRun) => sum + (legRun.item_count ?? 0), 0),
          source: row,
        });
      }
      if (tripsOfRow.length > 0) {
        // 回転の占有区間は、その回転の全バーを覆う範囲
        const starts = tripsOfRow.map((trip) => Date.parse(trip.span.occupyStartAt));
        const ends = tripsOfRow.map((trip) => Date.parse(trip.span.occupyEndAt));
        bucket.rotations.push({
          trips: tripsOfRow,
          occupyStartAt: new Date(Math.min(...starts)).toISOString(),
          occupyEndAt: new Date(Math.max(...ends)).toISOString(),
        });
      }
      buckets.set(key, bucket);
    }
  }

  // 2) 占有区間が重なる運行はレーンを分ける
  const lanes: Lane[] = [];
  for (const [key, bucket] of buckets) {
    const sorted = [...bucket.rotations].sort(
      (a, b) => Date.parse(a.occupyStartAt) - Date.parse(b.occupyStartAt),
    );
    const split: Rotation[][] = [];
    for (const rotation of sorted) {
      const slot = split.find((placed) => placed.every((other) => !overlaps(other, rotation)));
      if (slot) slot.push(rotation);
      else split.push([rotation]);
    }
    if (split.length === 0) split.push([]);

    split.forEach((rotations, index) => {
      const trips = rotations.flatMap((rotation) => rotation.trips);
      lanes.push({
        laneId: `${key}:${index}`,
        truckId: bucket.truckId,
        driverId: bucket.driverId,
        vehicle: bucket.vehicle,
        workTargets: [],
        trips,
        // 未計画の回転は先頭レーンにだけ載せる（分割レーンに複製しない）
        unplannedRows: index === 0 ? bucket.unplannedRows : [],
      });
    });
  }

  // 3) WorkTarget は targetRowId のバーが載っているレーンへ 1 回だけ。
  //    そのバーが無ければ（運行を持たない勤務）、同じ (truckId, driverId) の先頭レーンへ。
  //    全レーンへ複製すると同じ shiftTruckId の droppable が 2 つ描かれる。
  const laneKeyByShiftTruckId = buildLaneKeyByShiftTruckId(groups);
  for (const target of workTargets) {
    const owner = lanes.find((lane) => lane.trips.some((trip) => trip.rowId === target.targetRowId));
    if (owner) {
      owner.workTargets.push(target);
      continue;
    }
    const laneKey = laneKeyByShiftTruckId.get(target.shiftTruckId);
    const fallback = laneKey == null
      ? undefined
      : lanes.find((lane) => laneKeyOf(lane.truckId, lane.driverId) === laneKey);
    fallback?.workTargets.push(target);
  }

  return lanes.filter(
    (lane) => lane.trips.length > 0 || lane.workTargets.length > 0 || lane.unplannedRows.length > 0,
  );
}

/**
 * 車両表示順（ドラッグ後の楽観的な並び）でレーンを並べ替える。
 *
 * 並べ替えの単位はトラックで、レーンは `(truckId, driverId)` 単位。同じトラックのレーンは
 * 隣り合ったまま動く（安定ソートなので、トラック内の元の並びは保つ）。
 * 並び順に出てこないトラックは末尾へ（`/map` の `orderedRowGroups` と同じ扱い）。
 *
 * 左カラムだけでなくレーンそのものを並べ替えるのは、中央のボードと行が 1 対 1 で
 * 並ぶ画面だから。片方だけ並べ替えると全行が別の車両の運行の横に並ぶ。
 */
export function orderLanesByTruckIds(lanes: Lane[], truckIds: readonly number[]): Lane[] {
  if (truckIds.length === 0) return lanes;
  const orderIndex = new Map(truckIds.map((id, index) => [id, index]));
  // 並び順に無いトラックは列の後ろへ回す。差で比較するので Infinity は使えない（NaN になる）
  const unknownOrder = truckIds.length;
  return lanes
    .map((lane, index) => ({ lane, index, order: orderIndex.get(lane.truckId) ?? unknownOrder }))
    .sort((a, b) => (a.order - b.order) || (a.index - b.index))
    .map((it) => it.lane);
}
